import { useAtomValue, useStore } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { audioEngine } from "$/modules/audio/audio-engine.ts";
import { loadedAudioAtom } from "$/modules/audio/states/index.ts";
import {
	lyricLinesAtom,
	selectedLinesAtom,
	selectedWordsAtom,
	toolModeAtom,
} from "$/states/main.ts";
import type { TTMLLyric } from "$/types/ttml.ts";
import packageJson from "../../../package.json";
import {
	type CollaborationMessage,
	type CollaborationPeer,
	type CollaborationServerMessage,
	getCollaborationUrl,
	makeClientId,
} from "./protocol.ts";

export type CollaborationStatus =
	| "idle"
	| "connecting"
	| "connected"
	| "offline"
	| "error";

export const useCollaboration = (
	room: string | null,
	key: string | null,
	nickname = "Editor",
) => {
	const store = useStore();
	const mode = useAtomValue(toolModeAtom);
	const socketRef = useRef<WebSocket | null>(null);
	const clientIdRef = useRef(makeClientId());
	const revisionRef = useRef(0);
	const applyingRemoteRef = useRef(false);
	const audioChunksRef = useRef<Map<number, string>>(new Map());
	const audioMetaRef = useRef<{
		audioId: string;
		mime: string;
		size: number;
	} | null>(null);
	const audioGenerationRef = useRef(0);
	const [status, setStatus] = useState<CollaborationStatus>("idle");
	const [peers, setPeers] = useState<CollaborationPeer[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [isHost, setIsHost] = useState(false);
	const [serverRoomName, setServerRoomName] = useState<string | null>(null);
	const applyRemoteDocument = useCallback(
		(document: TTMLLyric) => {
			const lineIds = new Set(document.lyricLines.map((line) => line.id));
			const wordIds = new Set(
				document.lyricLines.flatMap((line) =>
					line.words.map((word) => word.id),
				),
			);
			store.set(lyricLinesAtom, document);
			store.set(
				selectedLinesAtom,
				new Set(
					[...store.get(selectedLinesAtom)].filter((id) => lineIds.has(id)),
				),
			);
			store.set(
				selectedWordsAtom,
				new Set(
					[...store.get(selectedWordsAtom)].filter((id) => wordIds.has(id)),
				),
			);
		},
		[store],
	);

	const send = useCallback((message: CollaborationMessage) => {
		const socket = socketRef.current;
		if (socket?.readyState === WebSocket.OPEN)
			socket.send(JSON.stringify(message));
	}, []);
	const sendAudio = useCallback(async () => {
		if (socketRef.current?.readyState !== WebSocket.OPEN) return;
		const blob = store.get(loadedAudioAtom);
		if (!blob.size || blob.size > 25 * 1024 * 1024) {
			audioGenerationRef.current += 1;
			return;
		}
		const generation = ++audioGenerationRef.current;
		const audioId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
		send({
			type: "audio-start",
			clientId: clientIdRef.current,
			audioId,
			mime: blob.type || "audio/mpeg",
			size: blob.size,
		});
		const bytes = new Uint8Array(await blob.arrayBuffer());
		const chunkSize = 48 * 1024;
		for (
			let offset = 0, index = 0;
			offset < bytes.length;
			offset += chunkSize, index++
		) {
			if (generation !== audioGenerationRef.current) return;
			const chunk = bytes.subarray(
				offset,
				Math.min(offset + chunkSize, bytes.length),
			);
			let binary = "";
			for (const byte of chunk) binary += String.fromCharCode(byte);
			send({
				type: "audio-chunk",
				clientId: clientIdRef.current,
				audioId,
				index,
				data: btoa(binary),
			});
		}
		if (generation === audioGenerationRef.current)
			send({ type: "audio-end", clientId: clientIdRef.current, audioId });
	}, [send, store]);

	useEffect(() => {
		if (!room || !key) {
			setStatus("idle");
			return;
		}
		setStatus("connecting");
		setError(null);
		const socket = new WebSocket(
			getCollaborationUrl(room, key, clientIdRef.current, packageJson.version),
		);
		socketRef.current = socket;
		socket.onopen = () => {
			setStatus("connected");
			send({ type: "hello", clientId: clientIdRef.current });
			send({
				type: "presence",
				clientId: clientIdRef.current,
				name: nickname || "Editor",
				mode: store.get(toolModeAtom),
				version: packageJson.version,
			});
		};
		socket.onmessage = async (event) => {
			try {
				const message = JSON.parse(
					String(event.data),
				) as CollaborationServerMessage;
				if (message.type === "welcome") {
					setIsHost(message.hostClientId === clientIdRef.current);
					setServerRoomName(message.roomName ?? null);
					revisionRef.current = message.revision;
					setPeers(
						message.peers.filter(
							(peer) => peer.clientId !== clientIdRef.current,
						),
					);
					if (message.document) {
						applyingRemoteRef.current = true;
						applyRemoteDocument(message.document);
						applyingRemoteRef.current = false;
					} else {
						const document = store.get(lyricLinesAtom);
						revisionRef.current = Math.max(1, revisionRef.current + 1);
						send({
							type: "update",
							clientId: clientIdRef.current,
							revision: revisionRef.current,
							document,
						});
					}
				} else if (
					message.type === "update" &&
					message.clientId !== clientIdRef.current
				) {
					revisionRef.current = Math.max(revisionRef.current, message.revision);
					applyingRemoteRef.current = true;
					applyRemoteDocument(message.document);
					applyingRemoteRef.current = false;
				} else if (message.type === "presence") {
					setIsHost(message.hostClientId === clientIdRef.current);
					setPeers(
						message.peers.filter(
							(peer) => peer.clientId !== clientIdRef.current,
						),
					);
				} else if (message.type === "audio-request") {
					void sendAudio();
				} else if (message.type === "audio-start") {
					audioMetaRef.current = {
						audioId: message.audioId,
						mime: message.mime,
						size: message.size,
					};
					audioChunksRef.current.clear();
				} else if (message.type === "audio-chunk") {
					if (audioMetaRef.current?.audioId === message.audioId)
						audioChunksRef.current.set(message.index, message.data);
				} else if (message.type === "audio-end") {
					const meta = audioMetaRef.current;
					if (meta?.audioId === message.audioId) {
						const chunks = [...audioChunksRef.current.entries()].sort(
							([a], [b]) => a - b,
						);
						const parts = chunks.map(([, data]) =>
							Uint8Array.from(atob(data), (char) => char.charCodeAt(0)),
						);
						const blob = new Blob(parts, { type: meta.mime });
						if (blob.size === meta.size) {
							applyingRemoteRef.current = true;
							await audioEngine.loadMusic(blob);
							applyingRemoteRef.current = false;
						}
					}
				} else if (message.type === "room-closed") {
					setIsHost(false);
					setStatus("offline");
					setError("The host closed this room.");
				} else if (message.type === "room-renamed") {
					setServerRoomName(message.name);
				} else if (message.type === "error") {
					setError(message.message);
					setStatus("error");
				}
			} catch {
				setError("The collaboration server sent an invalid message.");
			}
		};
		socket.onerror = () => {
			setStatus("error");
			setError("Could not connect to the collaboration room.");
		};
		socket.onclose = () => {
			if (socketRef.current === socket) setStatus("offline");
		};
		return () => {
			socket.close();
			if (socketRef.current === socket) socketRef.current = null;
		};
	}, [applyRemoteDocument, key, nickname, room, send, sendAudio, store]);

	useEffect(() => {
		if (!room || !key) return;
		send({
			type: "presence",
			clientId: clientIdRef.current,
			name: nickname || "Editor",
			mode,
			version: packageJson.version,
		});
	}, [key, mode, nickname, room, send]);

	useEffect(() => {
		if (!room) return;
		return store.sub(lyricLinesAtom, () => {
			if (
				applyingRemoteRef.current ||
				socketRef.current?.readyState !== WebSocket.OPEN
			)
				return;
			revisionRef.current += 1;
			send({
				type: "update",
				clientId: clientIdRef.current,
				revision: revisionRef.current,
				document: store.get(lyricLinesAtom),
			});
		});
	}, [room, send, store]);

	useEffect(() => {
		if (!room || !key) return;
		return store.sub(loadedAudioAtom, () => {
			if (!applyingRemoteRef.current) void sendAudio();
		});
	}, [key, room, sendAudio, store]);

	const closeRoom = useCallback(() => {
		if (!isHost) return;
		send({ type: "close-room", clientId: clientIdRef.current });
	}, [isHost, send]);
	const renameRoom = useCallback(
		(name: string) => {
			if (!isHost) return;
			send({ type: "rename-room", clientId: clientIdRef.current, name });
		},
		[isHost, send],
	);

	return {
		status,
		peers,
		peerCount: peers.length,
		error,
		isHost,
		closeRoom,
		renameRoom,
		serverRoomName,
	};
};
